import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { pickErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { notifyError, notifySuccess } from "@/lib/notify";

export default function AssignTest() {
  const { admin } = useAuth();
  const schoolId = admin?.school_id;
  const [searchParams] = useSearchParams();
  const preselectStudent = searchParams.get("student_id") || "";

  const [students, setStudents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [studentId, setStudentId] = useState(preselectStudent);
  const [categoryId, setCategoryId] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (preselectStudent) setStudentId(preselectStudent);
  }, [preselectStudent]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const calls = [api.get("/students"), api.get("/categories?active_only=true")];
        if (schoolId) calls.push(api.get(`/coupons/${schoolId}`));
        const [stuRes, catRes, coupRes] = await Promise.all(calls);
        if (cancelled) return;
        const stu = Array.isArray(stuRes.data) ? stuRes.data : [];
        setStudents(schoolId ? stu.filter((s) => s.school_id === schoolId) : stu);
        setCategories(Array.isArray(catRes.data) ? catRes.data : []);
        setCoupons(coupRes ? (Array.isArray(coupRes.data) ? coupRes.data : []) : []);
      } catch (err) {
        if (!cancelled) setLoadError(pickErr(err, "Failed to load form data"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess(null);
    setSubmitting(true);
    try {
      const { data } = await api.post("/assign-test", {
        student_id: studentId,
        category_id: categoryId,
        coupon_code: couponCode,
      });
      setSuccess(data);
      setStudentId("");
      setCategoryId("");
      setCouponCode("");
      notifySuccess("Test assigned successfully");
    } catch (err) {
      const msg = pickErr(err, "Failed to assign test");
      setError(msg);
      notifyError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Assign Test</h2>
      <p className="text-sm text-slate-500 mb-6">
        Pick a student, a category and a valid coupon code.
      </p>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : loadError ? (
        <p data-testid="assign-load-error" className="text-sm text-red-600">
          {loadError}
        </p>
      ) : (
        <section className="bg-white border border-slate-200 rounded-lg p-5 max-w-2xl">
          <form
            onSubmit={onSubmit}
            className="space-y-4"
            data-testid="assign-test-form"
          >
            <div>
              <label className="block text-xs text-slate-600 mb-1">
                Student
              </label>
              <select
                required
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                data-testid="assign-student"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                <option value="">Select student</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} — {s.class_name} (Roll {s.roll_number})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-slate-600 mb-1">
                Category
              </label>
              <select
                required
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                data-testid="assign-category"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs text-slate-600 mb-1">
                Coupon code
              </label>
              <select
                required
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                data-testid="assign-coupon"
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
              >
                <option value="">Select coupon</option>
                {coupons.map((c) => {
                  const today = new Date().toISOString().slice(0, 10);
                  const expired = c.expiry_date && c.expiry_date < today;
                  const exhausted = c.remaining <= 0;
                  const disabled = expired || exhausted;
                  const niceDate = (() => {
                    if (!c.expiry_date) return "";
                    const d = new Date(c.expiry_date);
                    return d.toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    });
                  })();
                  const label = expired
                    ? `${c.code} (Expired on ${niceDate})`
                    : exhausted
                    ? `${c.code} — fully used`
                    : `${c.code} — ${c.remaining} of ${c.usage_limit} remaining (exp ${niceDate})`;
                  return (
                    <option
                      key={c.code}
                      value={c.code}
                      disabled={disabled}
                      data-testid={`assign-coupon-opt-${c.code}`}
                    >
                      {label}
                    </option>
                  );
                })}
              </select>
              {coupons.length === 0 ? (
                <p className="mt-1 text-xs text-slate-500">
                  No coupons available for your school.
                </p>
              ) : null}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting}
                data-testid="assign-submit"
                className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
              >
                {submitting ? "Assigning…" : "Assign test"}
              </button>
              {error ? (
                <p data-testid="assign-error" className="text-sm text-red-600">
                  {error}
                </p>
              ) : null}
            </div>

            {success ? (
              <div
                data-testid="assign-success"
                className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800"
              >
                {success.message}. Coupon remaining:{" "}
                <strong>{success.coupon_remaining}</strong>
              </div>
            ) : null}
          </form>
        </section>
      )}
    </div>
  );
}
